/**
 * Firebase-backed token provider — the production auth seam. The ID tokens it
 * hands out are verified by the API (`verifyIdToken` in
 * `packages/adapters/src/firebaseAuth.ts`).
 *
 * The SDK is imported dynamically inside `ensureInitialized` — firebase never
 * reaches the marketing chunk, and module evaluation touches no browser API
 * (prerender-safe).
 */
import type { Auth } from "firebase/auth";

import { sessionScopedAuth } from "./sessionHandle";
import {
  ANONYMOUS_SNAPSHOT,
  LOADING_SNAPSHOT,
  UNAVAILABLE_SNAPSHOT,
  type AuthSnapshot,
  type TokenProvider,
} from "./token-provider";

/** Build the Firebase-backed {@link TokenProvider}. */
export function createFirebaseTokenProvider(): TokenProvider {
  let snapshot: AuthSnapshot = LOADING_SNAPSHOT;
  let authPromise: Promise<Auth> | null = null;
  const listeners = new Set<() => void>();

  function publish(next: AuthSnapshot): void {
    snapshot = next;
    for (const listener of listeners) listener();
  }

  async function initialize(): Promise<Auth> {
    const [auth, { onAuthStateChanged }] = await Promise.all([
      // Browser-wide, with a session the "Stay signed in" choice in force has
      // ended already signed out — see `sessionHandle.ts`. Doing it here
      // rather than after `authStateReady()` is what makes the first
      // published snapshot the truthful one.
      sessionScopedAuth(),
      import("firebase/auth"),
    ]);
    // Wait for the SDK to settle persisted state before publishing — reading
    // currentUser earlier misreports a signed-in user as anonymous.
    await auth.authStateReady();
    onAuthStateChanged(auth, (user) => {
      publish(user ? { status: "authenticated", providerUid: user.uid } : ANONYMOUS_SNAPSHOT);
    });
    // A tab left open is checked again when it comes back into view: a session
    // idle past the choice in force is signed out there, which reaches the
    // listener above; a good one counts the return as a visit. Registered once,
    // here, because this function runs to the end only once per page.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") void sessionScopedAuth().catch(() => undefined);
    });
    return auth;
  }

  /**
   * Start the SDK once, and **never memoise a failure**.
   *
   * A memo that kept a rejected promise would answer every later caller with
   * the same rejection, and both eager call sites below swallow it — so one
   * transient failure (the dynamic `firebase/app` import losing the network, a
   * throwing `firebaseWebConfig()`, a rejecting `authStateReady()`) would leave
   * the snapshot at `loading` forever, with every `/app` route spinning until a
   * full reload.
   *
   * So on rejection the memo is cleared, and the next caller genuinely retries;
   * and the snapshot moves to `unavailable`, because a cleared memo alone would
   * leave the UI spinning until something happened to ask again — the retry
   * fixes the *provider*, the publish fixes the *screen*.
   *
   * The rejection is re-thrown: `getIdToken()` and `signOut()` await this
   * promise and must keep failing loudly. Only the two eager, fire-and-forget
   * call sites swallow it.
   */
  function ensureInitialized(): Promise<Auth> {
    if (typeof window === "undefined") {
      return Promise.reject(new Error("[web] Firebase auth is client-side only"));
    }
    authPromise ??= initialize().catch((error: unknown) => {
      // Ordering note: this callback cannot run before the assignment above
      // completes — it is queued as a microtask at the earliest — so clearing
      // the field here always clears the memo it was meant to clear.
      authPromise = null;
      publish(UNAVAILABLE_SNAPSHOT);
      throw error;
    });
    return authPromise;
  }

  return {
    getSnapshot() {
      if (typeof window !== "undefined") void ensureInitialized().catch(() => undefined);
      return snapshot;
    },
    subscribe(listener) {
      listeners.add(listener);
      if (typeof window !== "undefined") void ensureInitialized().catch(() => undefined);
      return () => listeners.delete(listener);
    },
    async getIdToken() {
      const auth = await ensureInitialized();
      // A call made from a tab in view is someone using Reflect, so it is a
      // visit: a tab that stays in view and moves between pages without a
      // load would otherwise record none, and the next tab opened a day later
      // would end the session in use. It is checked first, like any visit: a
      // window left open on a shared machine must not hand whoever clicks
      // next a session the choice has ended, and reset its clock for them. A
      // hidden tab's calls (a poll) are neither.
      if (auth.currentUser !== null && document.visibilityState === "visible") {
        await sessionScopedAuth();
      }
      return (await auth.currentUser?.getIdToken()) ?? null;
    },
    async signOut() {
      const auth = await ensureInitialized();
      const { signOut } = await import("firebase/auth");
      await signOut(auth);
    },
  };
}
